import { t } from "../../../i18n";
import { CategoryIcon, DropdownMenu, Icon, TapTarget } from "../../ui";
import type { ExpenseCategory } from "../types";
import { CategoryBadge } from "./CategoryBadge";

interface CategoryPickerProps {
  categories: ExpenseCategory[];
  selected: ExpenseCategory | null;
  disabled?: boolean;
  onChange: (categoryId: string | null) => void;
}

/**
 * Per-row category dropdown. When `disabled` (e.g. settlement is
 * locked) we render a flat label instead of an interactive picker so
 * the row stays readable but is clearly inert.
 */
export function CategoryPicker({ categories, selected, disabled, onChange }: CategoryPickerProps) {
  if (disabled) {
    return (
      <span className="inline-flex h-8 items-center gap-2 px-2 text-ink-600 text-xs">
        {selected ? (
          <CategoryBadge category={selected} />
        ) : (
          <span className="text-ink-400">{t.settlement.expenses.table.uncategorized}</span>
        )}
      </span>
    );
  }
  return (
    <DropdownMenu
      align="start"
      trigger={
        <span className="inline-flex h-8 min-w-[10rem] max-w-[12rem] items-center gap-2 rounded-md border border-ink-200 bg-surface px-2 text-xs hover:bg-ink-50">
          {selected ? (
            <CategoryBadge category={selected} />
          ) : (
            <span className="truncate text-ink-400">
              {t.settlement.expenses.table.uncategorized}
            </span>
          )}
          <Icon name="chevron-down" size="xs" className="ml-auto text-ink-400" />
        </span>
      }
    >
      <TapTarget
        role="menuitem"
        onClick={() => onChange(null)}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-ink-700 text-sm hover:bg-ink-50"
      >
        <span className="h-5 w-5 rounded-md border border-ink-300 border-dashed" />
        <span className="text-ink-500">{t.settlement.expenses.table.uncategorized}</span>
      </TapTarget>
      {categories.map((c) => (
        <TapTarget
          key={c.id}
          role="menuitem"
          onClick={() => onChange(c.id)}
          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-ink-700 text-sm hover:bg-ink-50"
        >
          <span
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-white"
            style={{ backgroundColor: c.color }}
            aria-hidden="true"
          >
            <CategoryIcon name={c.icon ?? "tag"} size="xs" />
          </span>
          <span className="truncate">{c.name}</span>
        </TapTarget>
      ))}
    </DropdownMenu>
  );
}
